import { useEffect, useMemo, useState } from 'react';
import './PreviousInspectionPickerModal.css';

export type PreviousInspectionPickerColumn = {
  key: string;
  label: string;
  width?: string;
};

export type PreviousInspectionPickerRow = {
  id: number;
  cells: Record<string, string>;
};

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  columns: PreviousInspectionPickerColumn[];
  rows: PreviousInspectionPickerRow[];
  loading?: boolean;
  emptyText?: string;
  /** 같은 품목 등 우선 노출할 행 id */
  priorityIds?: number[];
  pageSize?: number;
  onClose: () => void;
  onSelect: (id: number) => void | Promise<void>;
};

const DEFAULT_PAGE_SIZE = 10;

function PreviousInspectionPickerModal({
  open,
  title,
  subtitle = '불러오면 품목·양식·요구사항(검사 항목)·측정값이 채워집니다. Lot No.·검사일·작성자는 새로 유지됩니다.',
  columns,
  rows,
  loading = false,
  emptyText = '이전 작성이 없습니다.',
  priorityIds,
  pageSize = DEFAULT_PAGE_SIZE,
  onClose,
  onSelect,
}: Props) {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [selectingId, setSelectingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setPage(0);
      setSelectingId(null);
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectingId == null) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, selectingId]);

  useEffect(() => {
    setPage(0);
  }, [keyword, rows, priorityIds]);

  const prioritySet = useMemo(() => new Set(priorityIds ?? []), [priorityIds]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const base = !q
      ? rows
      : rows.filter((row) =>
          columns.some((col) => (row.cells[col.key] || '').toLowerCase().includes(q))
        );
    if (prioritySet.size === 0) return base;
    return [...base].sort((a, b) => {
      const ap = prioritySet.has(a.id) ? 0 : 1;
      const bp = prioritySet.has(b.id) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return 0;
    });
  }, [rows, columns, keyword, prioritySet]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  if (!open) return null;

  const busy = loading || selectingId != null;

  return (
    <div
      className="prev-insp-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="prev-insp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prev-insp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="prev-insp-modal__head">
          <div>
            <p className="prev-insp-modal__eyebrow">이전 작성</p>
            <h3 id="prev-insp-modal-title">{title}</h3>
            <p className="prev-insp-modal__sub">{subtitle}</p>
          </div>
          <button
            type="button"
            className="prev-insp-modal__close"
            aria-label="닫기"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="prev-insp-modal__toolbar">
          <input
            type="search"
            className="prev-insp-modal__search"
            placeholder="품명·양식·Lot·상태로 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={busy}
          />
          <span className="prev-insp-modal__count">
            {filtered.length}/{rows.length}건
            {filtered.length > 0
              ? ` · ${safePage + 1}/${totalPages}페이지`
              : ''}
          </span>
        </div>

        <div className="prev-insp-modal__body">
          {busy && selectingId == null ? (
            <p className="prev-insp-modal__empty">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <p className="prev-insp-modal__empty">{emptyText}</p>
          ) : filtered.length === 0 ? (
            <p className="prev-insp-modal__empty">검색 결과가 없습니다.</p>
          ) : (
            <div className="prev-insp-modal__table-wrap">
              <table className="prev-insp-modal__table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                        {col.label}
                      </th>
                    ))}
                    <th style={{ width: 96 }} />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr
                      key={row.id}
                      className={prioritySet.has(row.id) ? 'is-priority' : undefined}
                      onClick={() => {
                        if (busy) return;
                        void (async () => {
                          try {
                            setSelectingId(row.id);
                            await onSelect(row.id);
                          } finally {
                            setSelectingId(null);
                          }
                        })();
                      }}
                    >
                      {columns.map((col) => (
                        <td key={col.key}>{row.cells[col.key] || '-'}</td>
                      ))}
                      <td>
                        <button
                          type="button"
                          className="prev-insp-modal__pick"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            void (async () => {
                              try {
                                setSelectingId(row.id);
                                await onSelect(row.id);
                              } finally {
                                setSelectingId(null);
                              }
                            })();
                          }}
                        >
                          {selectingId === row.id ? '불러오는 중…' : '불러오기'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filtered.length > pageSize ? (
          <div className="prev-insp-modal__pager">
            <button
              type="button"
              className="prev-insp-modal__pager-btn"
              disabled={busy || safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              이전
            </button>
            <span className="prev-insp-modal__pager-info">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="prev-insp-modal__pager-btn"
              disabled={busy || safePage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              다음
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default PreviousInspectionPickerModal;
